import { getLeaderboardXpGap } from "@memorang/helpers/leaderboard";
import { useState } from "react";
import { useAppTheme } from "../../useAppTheme";
import { useDevice } from "../../useDevice";
import Box from "../Box";
import Container from "../Container";
import { LeaderboardContent } from "./LeaderboardContent";
import { LeaderboardCurrentUserBar } from "./LeaderboardCurrentUserBar";
import { LeaderboardHeader } from "./LeaderboardHeader";
import { shouldShowCurrentUserBar } from "./helpers";
import type {
	LeaderboardConfig,
	LeaderboardFilterValue,
	LeaderboardItem,
	LeaderboardScope,
	LeaderboardTimePeriod,
} from "./types";

type Props = {
	items: LeaderboardItem[];
	currentUserItem: LeaderboardItem | undefined;
	timePeriod: LeaderboardTimePeriod;
	scope: LeaderboardScope;
	config: LeaderboardConfig;
	onFilterChange: (value: LeaderboardFilterValue) => void;
	isLoading?: boolean;
	error?: { title?: string; description?: string };
	insight?: { text?: string; isLoading?: boolean };
	onStart?: () => void;
	onRefresh?: () => void;
};

export const LeaderboardApplet = ({
	items,
	currentUserItem,
	timePeriod,
	scope,
	config,
	onFilterChange,
	isLoading = false,
	error,
	insight,
	onStart,
	onRefresh,
}: Props) => {
	const theme = useAppTheme();
	const { isMobile } = useDevice();
	const [isScrolled, setIsScrolled] = useState(false);

	const xpGap = getLeaderboardXpGap(items, currentUserItem);
	const showCurrentUserBar = shouldShowCurrentUserBar({
		currentUserItem,
		scope,
		numUsersToDisplay: config.numUsersToDisplay,
	});

	return (
		<Box flex={1}>
			<Box
				paddingHorizontal={16}
				paddingTop={isMobile ? 16 : 24}
				paddingBottom={16}
				style={{
					zIndex: 10,
					...(isScrolled && {
						shadowColor: theme.colors.shadow,
						shadowOffset: { width: 0, height: 2 },
						shadowOpacity: 0.15,
						shadowRadius: 4,
						elevation: 4,
					}),
				}}
			>
				<Box
					style={{
						width: "100%",
						maxWidth: theme.breakpoints?.sm,
						marginHorizontal: "auto",
					}}
				>
					<LeaderboardHeader
						timePeriod={timePeriod}
						scope={scope}
						timePeriods={config.timePeriods}
						scopes={config.scopes}
						onChange={onFilterChange}
					/>
				</Box>
			</Box>
			<Box flex={1} paddingHorizontal={isMobile ? 0 : 16}>
				<LeaderboardContent
					items={items}
					isLoading={isLoading}
					currentUserItem={currentUserItem}
					showUsersPodium={config.showUsersPodium}
					numUsersToDisplay={config.numUsersToDisplay}
					scope={scope}
					error={error}
					insight={insight}
					onStart={onStart}
					onRefresh={onRefresh}
					onScrolled={(y) => setIsScrolled(y > 0)}
				/>
			</Box>
			{showCurrentUserBar && currentUserItem ? (
				<Container autoSize>
					<LeaderboardCurrentUserBar
						{...currentUserItem}
						xpGap={xpGap}
						numUsersToDisplay={config.numUsersToDisplay}
					/>
				</Container>
			) : null}
		</Box>
	);
};
